import React from 'react';
import { Checkbox, Popover } from 'antd';

import { activeFilterCount, FilterGroup, FilterState } from './resultsView';
import styles from './styles.module.css';

interface AddFilterProps {
  groups: FilterGroup[];
  value: FilterState;
  onChange: (next: FilterState) => void;
}

const FunnelIcon: React.FC = () => (
  <svg width='12' height='12' viewBox='0 0 12 12' fill='none' aria-hidden>
    <path
      d='M1.5 2.5h9L7 6.5V10L5 9V6.5Z'
      stroke='currentColor'
      strokeWidth='1.3'
      fill='none'
    />
  </svg>
);

/** The "Add filter" pill and its checkbox popover. Group-agnostic, so Saved can reuse it. */
export const AddFilter: React.FC<AddFilterProps> = ({
  groups,
  value,
  onChange,
}) => {
  const count = activeFilterCount(value);
  const toggle = (key: string, option: string, checked: boolean): void => {
    const current = value[key] ?? [];
    onChange({
      ...value,
      [key]: checked
        ? [...current, option]
        : current.filter((v) => v !== option),
    });
  };

  const content = (
    <div className={styles.filterPopover}>
      {groups.map((group) => (
        <div key={group.key} className={styles.filterGroup}>
          <div className={styles.detailLabel}>{group.label}</div>
          {group.options.map((option) => (
            <Checkbox
              key={option.value}
              checked={(value[group.key] ?? []).includes(option.value)}
              onChange={(e) =>
                toggle(group.key, option.value, e.target.checked)
              }
            >
              {option.label}
            </Checkbox>
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <Popover
      trigger='click'
      placement='bottomLeft'
      arrow={false}
      content={content}
    >
      <button
        type='button'
        className={`${styles.filterPill} ${count > 0 ? styles.pillActive : ''}`}
      >
        <FunnelIcon />
        Add filter{count > 0 ? ` (${count})` : ''}
      </button>
    </Popover>
  );
};

interface FilterChipsProps {
  groups: FilterGroup[];
  value: FilterState;
  onChange: (next: FilterState) => void;
}

/** Removable chips for the active filters, plus Reset all. */
export const FilterChips: React.FC<FilterChipsProps> = ({
  groups,
  value,
  onChange,
}) => {
  const chips = groups.flatMap((group) =>
    (value[group.key] ?? []).map((selected) => ({
      key: `${group.key}:${selected}`,
      group: group.key,
      selected,
      text: `${group.label}: ${
        group.options.find((o) => o.value === selected)?.label ?? selected
      }`,
    }))
  );
  if (!chips.length) return null;
  return (
    <>
      {chips.map((chip) => (
        <span key={chip.key} className={styles.filterChip}>
          <span className={styles.chipText} title={chip.text}>
            {chip.text}
          </span>
          <button
            type='button'
            aria-label={`Remove ${chip.text}`}
            onClick={() =>
              onChange({
                ...value,
                [chip.group]: (value[chip.group] ?? []).filter(
                  (v) => v !== chip.selected
                ),
              })
            }
          >
            ×
          </button>
        </span>
      ))}
      <button
        type='button'
        className={styles.seeMore}
        onClick={() => onChange({})}
      >
        Reset all
      </button>
    </>
  );
};
